import React from 'react';
import styles from './Datatable.module.scss';
import Icon from '../Icons';
import Spinner from '../Spinner';
import Tooltip from '../Tooltip';

type Column<T> = {
  key: keyof T;
  label: string | React.ReactNode;
  render?: (value: any, row: T, rowIndex: number) => React.ReactNode;
  tooltip?: string;
  width?: string | number;
  show?: boolean;
};

type DatatableProps<T> = {
  columns: Column<T>[];
  data: T[];
  className?: string;
  emptyData?: React.ReactNode;
  isLoading?: boolean;
  page?: number;
  onChangeSize?: (size: number) => void;
  onChangePage?: (page: number) => void;
  totalPage: number;
};

const tableStyles = 'table';

function Datatable<T extends { [key: string]: any }>({
  className,
  columns,
  data,
  emptyData,
  isLoading = false,
  page,
  onChangeSize,
  onChangePage,
  totalPage = 1
}: DatatableProps<T>) {
  const [showData, setShowData] = React.useState(10);
  const [selectOpen, setSelectOpen] = React.useState(false);

  return (
    <div>
      <table className={[styles[tableStyles], className].join(' ')}>
        <thead>
          <tr>
            {columns.map((col) => (
              col?.show !== false && <th
                key={String(col.key)}
                style={{ width: col.width || 'auto' }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {col.label} {col.tooltip && (
                    <Tooltip message={col.tooltip} position="bottom_right" width="300px"
                      />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                {emptyData ? (
                  isLoading ? <Spinner /> : emptyData
                ) : 'No Data'}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col) => (
                  col?.show !== false && <td key={String(col.key)}
                    style={{ width: col.width || 'auto' }}>
                    {col.render
                      ? col.render(row[col.key], row, rowIndex)
                      : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className={styles[tableStyles + '__pagination']}>
        <div className={styles[tableStyles + '__pagination__count']}>
          <label htmlFor="data-count" style={{ cursor: 'pointer' }}>
            {showData} Data
            <Icon className={styles[tableStyles + '__icon_dropdown']} name="Chevron"              
              style={{
                transition: 'transform 0.2s',
                transform: selectOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}/>
          </label>
          <select
            name="data-count"
            id="data-count"
            onChange={e => {
              setShowData(Number(e.target.value));
              setSelectOpen(false);

              if (onChangeSize) {
                onChangeSize(Number(e.target.value));
              }
            }}
            onClick={() => setSelectOpen(true)}
            onMouseDown={() => setSelectOpen(false)}
            onReset={() => setSelectOpen(false)}
          >
            <option>10</option>
            <option>20</option>
            <option>50</option>
          </select>
        </div>
        <div className={styles[tableStyles + '__pagination__bullets']}>
          <Icon
            className={styles[tableStyles + '__icon_left']}
            data-testid="icon-left"
            name="Chevron"
            onClick={() => {
              if (onChangePage && page && page > 1) onChangePage(page - 1);
            }}
            style={{ cursor: page && page > 1 ? 'pointer' : 'not-allowed', opacity: page && page > 1 ? 1 : 0.5 }}
          />
          {Array.from({ length: totalPage ?? 1 }, (_, idx) => {
            // Show only neighbor pages: current, current-1, current+1, first, last
            if (
              idx + 1 === 1 ||
              idx + 1 === (totalPage ?? 1) ||
              (page && Math.abs(idx + 1 - page) <= 1)
            ) {
              return (
                <span
                  key={idx}
                  style={{
                    margin: '0 4px',
                    fontWeight: page === idx + 1 ? 700 : 400,
                    color: page === idx + 1 ? '#2970FF' : undefined,
                    cursor: 'pointer',
                    borderBottom: page === idx + 1 ? '2px solid #2970FF' : undefined,
                  }}
                  onClick={() => onChangePage && onChangePage(idx + 1)}
                >
                  {idx + 1}
                </span>
              );
            }
            // Show dots if skipping pages
            if (
              page &&
              ((idx === 1 && page > 3) ||
                (idx === (totalPage ?? 1) - 2 && page < (totalPage ?? 1) - 2))
            ) {
              return (
                <span key={idx} style={{ margin: '0 4px' }}>
                  ...
                </span>
              );
            }
            return null;
          })}
          <Icon
            className={styles[tableStyles + '__icon_right']}
            data-testid="icon-right"
            name="Chevron"
            onClick={() => {
              if (onChangePage && page && totalPage && page < totalPage) onChangePage(page + 1);
            }}
            style={{
              cursor: page && totalPage && page < totalPage ? 'pointer' : 'not-allowed',
              opacity: page && totalPage && page < totalPage ? 1 : 0.5,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default Datatable;
